import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
requiredRefs = ['overflowMenu'];

#state = { activeItem: null };
#submenuMutationObserver = null;
#observerTimer;

connectedCallback() {
super.connectedCallback();
this.addEventListener('click', (event) => {
  const link = event.composedPath().find(
    (element) =>
      element instanceof HTMLAnchorElement &&
      element.hasAttribute('href')
  );

  if (!link) return;

  const insideDropdown = event.composedPath().some(
    (element) =>
      element instanceof Element &&
      element.classList.contains('menu-list__submenu')
  );

  if (insideDropdown) {
    event.stopPropagation();
  }
}, true);

onDocumentLoaded(this.#preloadImages);
window.addEventListener('resize', this.#resizeListener);
document.addEventListener('click', this.#handleOutsideClick);
document.addEventListener('keydown', this.#handleEscape);
}

disconnectedCallback() {
super.disconnectedCallback();

window.removeEventListener('resize', this.#resizeListener);
document.removeEventListener('click', this.#handleOutsideClick);
document.removeEventListener('keydown', this.#handleEscape);

this.#cleanupMutationObserver();
}

#resizeListener = debounce(() => {
setHeaderMenuStyle();

if (this.#state.activeItem) {
this.#updateHeight();
}
}, 100);

get overflowMenu() {
return (
this.refs.overflowMenu?.shadowRoot?.querySelector(
'[part="overflow"]'
) ?? null
);
}

get headerComponent() {
return this.closest('header-component');
}

toggle = (event) => {
if (!(event.target instanceof Element)) return;

// Allow product and collection links to work normally.
if (event.target.closest('.menu-list__submenu')) return;

const listItem = event.target.closest(
'.menu-list__list-item'
);

if (!listItem) return;

const isMore = listItem.getAttribute('slot') === 'more';

const item = listItem.querySelector(
'[ref="menuitem"]'
);

if (!(item instanceof HTMLElement)) return;

const submenu = this.#findSubmenu(item);

// Normal links without dropdowns should navigate.
if (!submenu && !isMore) return;

event.preventDefault();

if (this.#state.activeItem === item) {
this.#deactivate();
} else {
this.#activate(item, listItem);
}
};

#handleEscape = (event) => {
if (
event.key !== 'Escape' ||
!this.#state.activeItem
) {
return;
}

const trigger = this.#state.activeItem;

this.#deactivate();
trigger.focus();
};

#handleOutsideClick = (event) => {
if (!this.#state.activeItem) return;

if (event.composedPath().includes(this)) return;

this.#deactivate();
};

#findSubmenu(item) {
const submenu = item?.parentElement?.querySelector(
'[ref="submenu[]"]'
);

return submenu instanceof HTMLElement
? submenu
: null;
}

#activate(item, listItem) {
if (!this.headerComponent) return;

this.dispatchEvent(new MegaMenuHoverEvent());

this.#deactivate();

const isDefaultSlot = !listItem.hasAttribute('slot');

const hasSubmenu = Boolean(
this.#findSubmenu(item)
);

const submenu =
this.#findSubmenu(item) ||
(!isDefaultSlot ? this.overflowMenu : null);

if (!submenu) return;

this.#state.activeItem = item;

this.dataset.overflowExpanded = String(
!isDefaultSlot
);

this.ariaExpanded = 'true';

item.setAttribute('aria-expanded', 'true');

submenu.dataset.active = '';

this.#cleanupMutationObserver();

this.#submenuMutationObserver = new MutationObserver(
() => {
requestAnimationFrame(() => {
requestAnimationFrame(() => {
if (this.#state.activeItem === item) {
this.#updateHeight();
}
});
});
}
);

this.#submenuMutationObserver.observe(listItem, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['aria-expanded'],
});

// Restore the open state if the theme resets it
// while the header is still loading.
const restoreOpenState = () => {
  if (this.#state.activeItem !== item) return;

  if (item.getAttribute('aria-expanded') !== 'true') {
    item.setAttribute('aria-expanded', 'true');
    submenu.dataset.active = '';
    this.#updateHeight();
  }
};

this.#submenuMutationObserver.disconnect();

this.#submenuMutationObserver = new MutationObserver(() => {
  restoreOpenState();
  this.#updateHeight();
});

this.#submenuMutationObserver.observe(listItem, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['aria-expanded'],
});

// Refresh the open dropdown when delayed content arrives.
requestAnimationFrame(() => {
  if (this.#state.activeItem !== item) return;

  this.#updateHeight();

  const inner = submenu.querySelector(
    '.menu-list__submenu-inner'
  );

  if (!inner) return;

  const resizeObserver = new ResizeObserver(() => {
    if (this.#state.activeItem === item) {
      this.#updateHeight();
    } else {
      resizeObserver.disconnect();
    }
  });

  resizeObserver.observe(inner);

  setTimeout(() => {
    resizeObserver.disconnect();
  }, 3000);
});

this.#observerTimer = setTimeout(() => {
this.#cleanupMutationObserver();
}, 3000);

this.#updateHeight();

this.style.setProperty(
  '--submenu-opacity',
  '1'
);

// Recalculate after delayed menu content has loaded.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    if (this.#state.activeItem === item) {
      this.#updateHeight();
    }
  });
});
}

#updateHeight() {
const item = this.#state.activeItem;

if (!item) return;

const listItem = item.closest(
'.menu-list__list-item'
);

const isDefaultSlot = !listItem?.hasAttribute(
'slot'
);

const submenu = this.#findSubmenu(item);

let height = submenu?.offsetHeight || 0;

if (!isDefaultSlot) {
const overflowHeight =
this.overflowMenu?.offsetHeight || 0;

const overflowListHeight =
this.#getOverflowListLinksHeight();

height = submenu
? Math.max(
overflowHeight,
overflowListHeight
)
: overflowListHeight;
}

this.headerComponent?.style.setProperty(
'--submenu-height',
`${height}px`
);

this.#setFullOpenHeaderHeight(height);
}

#deactivate() {
const item = this.#state.activeItem;

if (!item) return;

const submenu = this.#findSubmenu(item);

if (submenu) {
delete submenu.dataset.active;
}

if (this.overflowMenu) {
delete this.overflowMenu.dataset.active;
}

item.setAttribute('aria-expanded', 'false');

this.#state.activeItem = null;

this.ariaExpanded = 'false';

this.dataset.overflowExpanded = 'false';

this.headerComponent?.style.setProperty(
'--submenu-height',
'0px'
);

this.#setFullOpenHeaderHeight(0);

this.style.setProperty(
'--submenu-opacity',
'0'
);

this.#cleanupMutationObserver();
}

#getOverflowListLinksHeight() {
const slottedMenuLinks = this.overflowMenu
?.querySelector('slot')
?.assignedElements();

if (!slottedMenuLinks) {
return this.overflowMenu?.offsetHeight || 0;
}

const submenus = slottedMenuLinks
.map((link) =>
link.querySelector('[ref="submenu[]"]')
)
.filter(Boolean);

submenus.forEach((submenu) => {
submenu.style.setProperty(
'display',
'none'
);
});

const height =
this.overflowMenu?.offsetHeight || 0;

submenus.forEach((submenu) => {
submenu.style.removeProperty('display');
});

return height;
}

#setFullOpenHeaderHeight(submenuHeight) {
const header = this.headerComponent;

if (!header) return;

const overlap = header.hasAttribute(
'data-submenu-overlap-bottom-row'
);

const visibleHeight =
overlap && header.offsetHeight > 0
? header.querySelector(
'.header__row--top'
)?.offsetHeight ?? 0
: header.offsetHeight;

header.style.setProperty(
'--full-open-header-height',
`${
submenuHeight
? submenuHeight + visibleHeight
: 0
}px`
);
}

#preloadImages = () => {
this.querySelectorAll(
'img[loading="lazy"]'
).forEach((image) => {
image.removeAttribute('loading');
});
};

#cleanupMutationObserver() {
this.#submenuMutationObserver?.disconnect();

this.#submenuMutationObserver = null;

clearTimeout(this.#observerTimer);
}
}

if (!customElements.get('header-menu')) {
customElements.define(
'header-menu',
HeaderMenu
);
}